import { computed, ref, watch, watchEffect } from 'vue'
import { usePreferenceStore } from '../state/stores/preferenceStore'
import { getThemeById } from '../theme/theme'
import { ThemeDefinition } from '../theme/themeUtils'
import { EventBus } from '../utils/eventBus'

export function useTheme(options?: { alwaysFollowSystem?: boolean }) {
  const alwaysFollowSystem = options?.alwaysFollowSystem ?? false
  const preferenceStore = usePreferenceStore()
  const themeMode = computed(() => preferenceStore.localPreferences.themeMode)
  const preferredLightThemeId = computed(
    () => preferenceStore.localPreferences.preferredLightThemeId
  )
  const preferredDarkThemeId = computed(() => preferenceStore.localPreferences.preferredDarkThemeId)

  const systemPrefersDark = ref(false)
  const resolvedThemeMode = computed<'light' | 'dark'>(() => {
    // If alwaysFollowSystem is true, always use system preference
    if (alwaysFollowSystem) {
      return systemPrefersDark.value ? 'dark' : 'light'
    }
    // Otherwise, use the normal logic
    if (themeMode.value === 'system') {
      return systemPrefersDark.value ? 'dark' : 'light'
    } else if (themeMode.value === 'dark') {
      return 'dark'
    } else {
      return 'light'
    }
  })
  const activeThemeId = ref<string | null>(null)

  // --- System Theme Detection (Run directly, not in onMounted) ---
  let mediaQuery: MediaQueryList | null = null

  const updateSystemPreference = () => {
    if (mediaQuery) {
      systemPrefersDark.value = mediaQuery.matches
    }
  }

  // Setup listener immediately if in browser context
  if (typeof window !== 'undefined') {
    mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    updateSystemPreference() // Initial check
    mediaQuery.addEventListener('change', updateSystemPreference)
    // No onUnmounted needed here for this singleton pattern
  }

  // --- Active Theme Calculation ---
  watchEffect(() => {
    if (alwaysFollowSystem) {
      // Always use system preference with default themes
      activeThemeId.value = systemPrefersDark.value ? 'shadow' : 'zen'
    } else if (themeMode.value === 'system') {
      activeThemeId.value = systemPrefersDark.value
        ? preferredDarkThemeId.value
        : preferredLightThemeId.value
    } else if (themeMode.value === 'dark') {
      activeThemeId.value = preferredDarkThemeId.value
    } else {
      // themeMode.value === 'light'
      activeThemeId.value = preferredLightThemeId.value
    }
  })

  // --- Derived State ---
  const activeTheme = computed<ThemeDefinition | undefined>(() => {
    return activeThemeId.value ? getThemeById(activeThemeId.value) : undefined
  })

  const dark = computed(() => activeTheme.value?.type === 'dark')

  // --- Side Effects ---
  // Toggle Tailwind classx`
  watch(
    dark,
    (dark) => {
      if (typeof document !== 'undefined') {
        document.documentElement.classList.toggle('tw-dark', dark)
      }
    },
    { immediate: true } // Apply initial class
  )

  // --- Exposed Methods ---
  const setThemeMode = async (mode: 'light' | 'dark' | 'system') => {
    await preferenceStore.setThemeMode(mode)
    // Emit theme change event to notify other windows
    EventBus.emit('theme-changed', { themeMode: mode }, 'other')
  }

  const setPreferredLightTheme = async (themeId: string) => {
    await preferenceStore.setPreferredLightTheme(themeId)
    // Emit theme change event to notify other windows
    EventBus.emit('theme-changed', { preferredLightThemeId: themeId }, 'other')
  }

  const setPreferredDarkTheme = async (themeId: string) => {
    await preferenceStore.setPreferredDarkTheme(themeId)
    // Emit theme change event to notify other windows
    EventBus.emit('theme-changed', { preferredDarkThemeId: themeId }, 'other')
  }

  // Return reactive state and methods
  return {
    themeMode,
    resolvedThemeMode,
    preferredLightThemeId,
    preferredDarkThemeId,
    activeTheme,
    dark,
    setThemeMode,
    setPreferredLightTheme,
    setPreferredDarkTheme
  }
}
